<template>
    <transition name="insights-enter" appear>
        <div v-if="showBlock" class="insights-cards-block">
            <transition name="insights-expand" mode="out-in">
                <!-- Collapsed teaser (dashboard only) -->
                <div
                    v-if="collapsible && hasAnyInsights && !expanded"
                    key="teaser"
                    class="insights-cards-block__teaser"
                    role="button"
                    tabindex="0"
                    :aria-expanded="false"
                    :aria-controls="teaserAriaId"
                    @click="expanded = true"
                    @keydown.enter.prevent="expanded = true"
                    @keydown.space.prevent="expanded = true"
                >
                    <div class="insights-cards-block__teaser-inner">
                        <p class="insights-cards-block__teaser-text">{{ teaserHeadline }}</p>
                        <span class="insights-cards-block__teaser-cta">
                            {{ viewInsightsLabel }}
                            <i class="fe-chevron-down insights-cards-block__teaser-chevron"></i>
                        </span>
                    </div>
                </div>

                <!-- Full block (always when !collapsible; when collapsible only if expanded) -->
                <div v-else key="full" class="insights-cards-block__full">
            <header class="insights-cards-block__header">
                <div class="row">
                    <div class="col-12">
                        <div class="page-title-box">
                            <h4 class="page-title">{{ headerTitle }}</h4>
                            <p v-if="headerSubtitle" class="insights-cards-block__subtitle">{{ headerSubtitle }}</p>
                            <button
                                v-if="collapsible && expanded"
                                type="button"
                                class="insights-cards-block__collapse-btn"
                                :aria-expanded="true"
                                :aria-controls="teaserAriaId"
                                @click="expanded = false"
                            >
                                {{ collapseLabel }}
                                <i class="fe-chevron-up"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div v-if="loading" class="insights-cards-block__loading">
                <div class="insights-cards-block__spinner" aria-hidden="true"></div>
                <p class="insights-cards-block__loading-text">{{ loadingText }}</p>
            </div>

            <p v-else-if="error" class="insights-cards-block__error">{{ errorText }}</p>

            <div v-else :id="teaserAriaId" class="insights-cards-block__grid">
                <article
                    v-for="(block, key) in visibleAlertBlocks"
                    :key="key"
                    class="insight-card"
                    :class="`insight-card--${block.severity}`"
                    :style="{ animationDelay: block.delay }"
                >
                    <div class="insight-card__header">
                        <div class="insight-card__header-left">
                            <div class="insight-card__badge">{{ block.agentLabel }}</div>
                            <h2 class="insight-card__title">{{ block.title }}</h2>
                        </div>
                        <button
                            :id="`insight-card-menu-${key}`"
                            type="button"
                            class="insight-card__dots-btn"
                            :aria-label="viewRecordsLabel"
                            @click.prevent="openInsightModal(key)"
                        >
                            <i class="fe-more-horizontal icon-blue" aria-hidden="true" />
                        </button>
                    </div>
                    <p class="insight-card__description">{{ block.description }}</p>
                    <div class="insight-card__count">
                        <span class="insight-card__count-value">{{ summary[key] ? summary[key].count : 0 }}</span>
                        <span class="insight-card__count-label">{{ countLabel }}</span>
                    </div>
                    <div v-if="summary[key] && summary[key].items && summary[key].items.length" class="insight-card__preview">
                        <ul class="insight-card__list">
                            <li
                                v-for="(item, idx) in summary[key].items.slice(0, 5)"
                                :key="idx"
                                class="insight-card__list-item"
                            >
                                {{ item.name }}{{ item.external_code ? ` (#${item.external_code})` : '' }}
                            </li>
                        </ul>
                        <p v-if="summary[key].count > 5" class="insight-card__more">
                            {{ getMoreCountLabel(key) }}
                        </p>
                    </div>
                    <p v-else-if="summary[key] && summary[key].count === 0" class="insight-card__empty">
                        {{ emptyStateLabel }}
                    </p>
                </article>
            </div>
                </div>
        </transition>
        <insight-records-modal ref="insightRecordsModal" />
    </div>
</transition>
</template>

<script>
import InsightsAlertsService from '@src/services/insights/InsightsAlertsService'
import InsightRecordsModal from '@/src/components/dashboards/InsightRecordsModal.vue'

const i18nKey = 'INSIGHTS'

export default {
    name: 'InsightsCardsBlock',
    components: {
        InsightRecordsModal,
    },
    props: {
        collapsible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            error: null,
            expanded: false,
            summary: {
                promoters_without_roteiro_next_3_days: null,
                stores_not_visited_last_week_without_roteiro: null,
                stores_not_visited_this_week_with_roteiro: null,
                users_inactive_mobile_7_days: null,
            },
        }
    },
    computed: {
        showBlock() {
            if (!this.collapsible) return true
            if (this.loading || this.error) return false
            return this.hasAnyInsights
        },
        hasAnyInsights() {
            return this.insightCategoriesCount > 0
        },
        /** Number of insight categories that have at least one record. Used to decide if we show the block. */
        insightCategoriesCount() {
            const keys = this.insightCategoryKeys
            return keys.filter((key) => Number(this.summary[key]?.count ?? 0) > 0).length
        },
        insightCategoryKeys() {
            return [
                'promoters_without_roteiro_next_3_days',
                'stores_not_visited_last_week_without_roteiro',
                'stores_not_visited_this_week_with_roteiro',
                'users_inactive_mobile_7_days',
            ]
        },
        teaserAriaId() {
            return 'insights-cards-block-expandable'
        },
        teaserHeadline() {
            const count = this.insightCategoriesCount
            const key = this.makeI18nKey(i18nKey, 'MESSAGES.teaser_headline')
            console.log('count', count)
            return this.$t(key, { count })
        },
        viewInsightsLabel() {
            return this.getI18n(i18nKey, 'LABELS.view_insights') || 'Ver insights'
        },
        collapseLabel() {
            return this.getI18n(i18nKey, 'LABELS.collapse') || 'Recolher'
        },
        headerTitle() {
            return this.getI18n(i18nKey, 'TITLES.alerts_panel') || 'Insights'
        },
        headerSubtitle() {
            const count = this.insightCategoriesCount
            const key = this.makeI18nKey(i18nKey, 'TITLES.alerts_subtitle_with_count')
            return this.$t(key, { count })
        },
        loadingText() {
            return this.getI18n(i18nKey, 'MESSAGES.loading') || 'Carregando...'
        },
        errorText() {
            return this.getI18n(i18nKey, 'MESSAGES.error') || 'Erro ao carregar os alertas.'
        },
        countLabel() {
            return this.getI18n(i18nKey, 'LABELS.count') || 'registro(s)'
        },
        seeMoreLabel() {
            return this.getI18n(i18nKey, 'LABELS.see_more') || 'Ver mais'
        },
        emptyStateLabel() {
            return this.getI18n(i18nKey, 'MESSAGES.empty') || 'Nenhum registro.'
        },
        viewRecordsLabel() {
            return this.getI18n(i18nKey, 'LABELS.view_records_list') || 'Ver lista de registros'
        },
        alertBlocks() {
            return {
                promoters_without_roteiro_next_3_days: {
                    title: this.getI18n(i18nKey, 'ALERTS.promoters_without_roteiro_title') || 'Promotores sem roteiro nos próximos 3 dias',
                    description: this.getI18n(i18nKey, 'ALERTS.promoters_without_roteiro_description') || 'Usuários com perfil promotor/supervisor sem nenhum roteiro entre hoje e daqui a 3 dias.',
                    agentLabel: this.getI18n(i18nKey, 'ALERTS.insight_planning') || 'Insight de planejamento',
                    severity: 'warning',
                    delay: '0s',
                },
                stores_not_visited_last_week_without_roteiro: {
                    title: this.getI18n(i18nKey, 'ALERTS.stores_not_visited_title') || 'Lojas não visitadas na semana anterior e sem roteiro',
                    description: this.getI18n(i18nKey, 'ALERTS.stores_not_visited_description') || 'PDVs que na semana passada não tiveram visita nem roteiro planejado.',
                    agentLabel: this.getI18n(i18nKey, 'ALERTS.insight_coverage') || 'Insight de cobertura',
                    severity: 'info',
                    delay: '0.1s',
                },
                stores_not_visited_this_week_with_roteiro: {
                    title: this.getI18n(i18nKey, 'ALERTS.stores_not_visited_this_week_title') || 'Lojas que deviam ser visitadas nesta semana e não foram',
                    description: this.getI18n(i18nKey, 'ALERTS.stores_not_visited_this_week_description') || 'PDVs com roteiro planejado nesta semana (até ontem) que ainda não foram visitados.',
                    agentLabel: this.getI18n(i18nKey, 'ALERTS.insight_coverage') || 'Insight de cobertura',
                    severity: 'info',
                    delay: '0.15s',
                },
                users_inactive_mobile_7_days: {
                    title: this.getI18n(i18nKey, 'ALERTS.users_inactive_title') || 'Usuários há mais de 7 dias sem logar no app',
                    description: this.getI18n(i18nKey, 'ALERTS.users_inactive_description') || 'Promotores ou supervisores sem atividade no app móvel nos últimos 7 dias.',
                    agentLabel: this.getI18n(i18nKey, 'ALERTS.insight_engagement') || 'Insight de engajamento',
                    severity: 'attention',
                    delay: '0.2s',
                },
            }
        },
        /** Only alert blocks that have at least one record (count > 0). Cards with 0 are not displayed. */
        visibleAlertBlocks() {
            const blocks = this.alertBlocks
            return this.insightCategoryKeys
                .filter((key) => Number(this.summary[key]?.count ?? 0) > 0)
                .reduce((acc, key) => {
                    acc[key] = blocks[key]
                    return acc
                }, {})
        },
    },
    mounted() {
        this.fetchAlerts()
    },
    methods: {
        /** Returns "e mais X" where X = total records minus the number we display (up to 5). */
        getMoreCountLabel(key) {
            const total = Number(this.summary[key]?.count ?? 0)
            const items = this.summary[key]?.items
            const displayed = Math.min(5, Array.isArray(items) ? items.length : 0)
            const more = Math.max(0, total - displayed)
            return this.formatMoreCount(more)
        },
        formatMoreCount(count) {
            const key = this.makeI18nKey(i18nKey, 'LABELS.more_count')
            return this.$t(key, { count })
        },
        async fetchAlerts() {
            this.loading = true
            this.error = null
            try {
                const { data } = await InsightsAlertsService.fetchAlerts({})
                this.summary = this.normalizeSummaryResponse(data)
            } catch (e) {
                this.error = true
                this.summary = {
                    promoters_without_roteiro_next_3_days: { count: 0, items: [] },
                    stores_not_visited_last_week_without_roteiro: { count: 0, items: [] },
                    stores_not_visited_this_week_with_roteiro: { count: 0, items: [] },
                    users_inactive_mobile_7_days: { count: 0, items: [] },
                }
            } finally {
                this.loading = false
            }
        },
        /**
         * Normalize API response: unwrap optional "data" and ensure each category has numeric count and array items.
         */
        openInsightModal(key) {
            const block = this.alertBlocks[key]
            const summarySlot = this.summary[key]
            const count = summarySlot ? Number(summarySlot.count) : 0
            const initialItems = summarySlot && Array.isArray(summarySlot.items) ? summarySlot.items : []
            this.$refs.insightRecordsModal.showModal({
                insightKey: key,
                title: block ? block.title : '',
                count,
                initialItems,
                filterParams: {},
            })
        },
        normalizeSummaryResponse(data) {
            const raw = data?.data ?? data
            if (!raw || typeof raw !== 'object') {
                return {
                    promoters_without_roteiro_next_3_days: { count: 0, items: [] },
                    stores_not_visited_last_week_without_roteiro: { count: 0, items: [] },
                    stores_not_visited_this_week_with_roteiro: { count: 0, items: [] },
                    users_inactive_mobile_7_days: { count: 0, items: [] },
                }
            }
            const keys = this.insightCategoryKeys
            const normalized = {}
            keys.forEach((k) => {
                const slot = raw[k]
                normalized[k] = {
                    count: Number(slot?.count ?? 0),
                    items: Array.isArray(slot?.items) ? slot.items : [],
                }
            })
            return normalized
        },
    },
}
</script>

<style scoped>
/* Platform alignment: Exo2-Regular, #404249 title, #6c757d secondary, #82B2F3 action (see _page-head.scss, _variables.scss, icons.scss) */
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Instrument+Sans:wght@500;600;700&display=swap');

.insights-cards-block {
    --insights-font-display: 'Instrument Sans', 'DM Sans', system-ui, sans-serif;
    --insights-font-body: 'Figtree', 'Source Sans 3', system-ui, sans-serif;
    --insights-ink: #1a1a1a;
    --insights-ink-muted: #5c5c5c;
    --insights-card-bg: #fff;
    --insights-border: #e7e5e4;
    --insights-warning: #b45309;
    --insights-warning-bg: #fffbeb;
    --insights-info: #0e7490;
    --insights-info-bg: #ecfeff;
    --insights-attention: #be123c;
    --insights-attention-bg: #fff1f2;

    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
}

/* ---- Entrance: block appears with smooth fade + slide (Vue 2: enter, enter-to) ---- */
.insights-enter-enter {
    opacity: 0;
    transform: translateY(-12px);
}
.insights-enter-enter-to {
    opacity: 1;
    transform: translateY(0);
}
.insights-enter-enter-active {
    transition: opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Same as enter for initial mount (appear) */
.insights-enter-appear {
    opacity: 0;
    transform: translateY(-12px);
}
.insights-enter-appear-to {
    opacity: 1;
    transform: translateY(0);
}
.insights-enter-appear-active {
    transition: opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.insights-enter-leave {
    opacity: 1;
    transform: translateY(0);
}
.insights-enter-leave-to {
    opacity: 0;
    transform: translateY(-12px);
}
.insights-enter-enter-active {
    transition: opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.insights-enter-leave-active {
    transition: opacity 0.28s ease-in, transform 0.28s ease-in;
}

/* ---- Expand: collapsed → expanded feels like block "growing out" ---- */
.insights-expand-enter {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
}
.insights-expand-enter-to {
    opacity: 1;
    transform: translateY(0) scale(1);
}
.insights-expand-leave {
    opacity: 1;
    transform: translateY(0) scale(1);
}
.insights-expand-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.99);
}
.insights-expand-enter-active {
    transition: opacity 0.4s cubic-bezier(0.22, 1, 0.36, 1), transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top center;
}
.insights-expand-leave-active {
    transition: opacity 0.32s cubic-bezier(0.4, 0, 0.2, 1), transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: top center;
}
.insights-cards-block__full {
    transform-origin: top center;
}

/* ---- Header: platform page-title (Exo2-Regular, 16px, #404249) ---- */
.insights-cards-block__header {
    margin-bottom: 1rem;
}

.insights-cards-block__header .page-title-box {
    margin-bottom: 0;
}

.insights-cards-block__header .page-title {
    font-family: 'Exo2-Regular', sans-serif;
    font-size: 16px;
    color: #404249;
    letter-spacing: 0.02em;
}

.insights-cards-block__subtitle {
    margin: 0.25rem 0 0 0;
    font-family: 'Exo2-Regular', sans-serif;
    font-size: 0.875rem;
    line-height: 1.4;
    color: #6c757d;
}

.insights-cards-block__collapse-btn {
    display: inline-flex;
    gap: 0.35rem;
    align-items: center;
    padding: 0.25rem 0.5rem;
    margin-top: 0.5rem;
    font-family: 'Exo2-Regular', sans-serif;
    font-size: 0.875rem;
    color: #82B2F3;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 6px;
    transition: color 0.2s ease, background 0.2s ease;
}

.insights-cards-block__collapse-btn:hover {
    color: #2e7feb;
    background: rgba(130, 178, 243, 0.08);
}

.insights-cards-block__collapse-btn .fe-chevron-up {
    font-size: 0.9em;
}

/* ---- Teaser: platform card style + Exo2 ---- */
.insights-cards-block__teaser {
    padding: 1rem 1.25rem;
    cursor: pointer;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.25s ease, border-color 0.25s ease, background 0.2s ease;
}

.insights-cards-block__teaser:hover {
    border-color: #ced4da;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.insights-cards-block__teaser-inner {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
}

.insights-cards-block__teaser-text {
    margin: 0;
    font-family: 'Exo2-Regular', sans-serif;
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.4;
    color: #404249;
}

.insights-cards-block__teaser-cta {
    display: inline-flex;
    gap: 0.35rem;
    align-items: center;
    font-family: 'Exo2-Regular', sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    color: #82B2F3;
    transition: color 0.2s ease;
}

.insights-cards-block__teaser:hover .insights-cards-block__teaser-cta {
    color: #2e7feb;
}

.insights-cards-block__teaser-chevron {
    font-size: 1rem;
    opacity: 0.9;
}

.insights-cards-block__loading,
.insights-cards-block__error {
    padding: 2rem 1rem;
    font-family: var(--insights-font-body);
    color: var(--insights-ink-muted);
    text-align: center;
}

.insights-cards-block__spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 1rem;
    border: 3px solid var(--insights-border);
    border-top-color: var(--insights-ink);
    border-radius: 50%;
    animation: insights-spin 0.8s linear infinite;
}

@keyframes insights-spin {
    to { transform: rotate(360deg); }
}

.insights-cards-block__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
}

.insight-card {
    padding: 1.5rem;
    font-family: var(--insights-font-body);
    background: var(--insights-card-bg);
    border: 1px solid var(--insights-border);
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    animation: insights-reveal 0.5s ease-out both;
}

.insight-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
}

.insight-card__header {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.insight-card__header-left {
    flex: 1;
    min-width: 0;
}

.insight-card__dots-btn {
    flex-shrink: 0;
    padding: 0.25rem;
    margin: -0.25rem -0.25rem 0 0;
    color: #82B2F3;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 6px;
    transition: color 0.2s ease, background 0.2s ease;
}

.insight-card__dots-btn:hover {
    color: #2e7feb;
    background: rgba(130, 178, 243, 0.08);
}

.insight-card__dots-btn .icon-blue {
    font-size: 1.25rem;
}

.insight-card__badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    margin-bottom: 0.75rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-radius: 6px;
}

.insight-card--warning .insight-card__badge { color: var(--insights-warning); background: var(--insights-warning-bg); }
.insight-card--info .insight-card__badge { color: var(--insights-info); background: var(--insights-info-bg); }
.insight-card--attention .insight-card__badge { color: var(--insights-attention); background: var(--insights-attention-bg); }

.insight-card__title {
    margin: 0 0 0.5rem 0;
    font-family: var(--insights-font-display);
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.3;
    color: var(--insights-ink);
}

.insight-card__description {
    margin: 0 0 1rem 0;
    font-size: 0.9rem;
    line-height: 1.5;
    color: var(--insights-ink-muted);
}

.insight-card__count {
    margin-bottom: 1rem;
}

.insight-card__count-value {
    font-family: var(--insights-font-display);
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--insights-ink);
}

.insight-card__count-label {
    margin-left: 0.35rem;
    font-size: 0.85rem;
    color: var(--insights-ink-muted);
}

.insight-card__preview {
    padding-top: 1rem;
    border-top: 1px solid var(--insights-border);
}

.insight-card__list {
    padding: 0;
    margin: 0 0 0.5rem 0;
    font-size: 0.85rem;
    color: var(--insights-ink-muted);
    list-style: none;
}

.insight-card__list-item {
    position: relative;
    padding: 0.2rem 0;
    padding-left: 1rem;
}

.insight-card__list-item::before {
    position: absolute;
    top: 0.6em;
    left: 0;
    width: 4px;
    height: 4px;
    content: '';
    background: var(--insights-ink-muted);
    border-radius: 50%;
}

.insight-card__more {
    margin: 0;
    font-size: 0.8rem;
    color: var(--insights-ink-muted);
}

.insight-card__empty {
    margin: 0;
    font-size: 0.9rem;
    font-style: italic;
    color: var(--insights-ink-muted);
}

@keyframes insights-reveal {
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
